import React, { useMemo, useState, useCallback } from "react";
import BasicCRUD from "../core/basic-crud";

const dict = {
    en: {
        title: "Change History",
        subTitle: "actions performed in the workspace",
        homeURL: "/dashboard",
        searchPlaceholder: "Search by user or action...",
        filterLabel: "Entity",
        allEntities: "All entities",
        entityWorkspace: "Workspaces",
        entityDomain: "Domains",
        entityCertificate: "Certificates",
        entityWebsite: "Web Sites",
        entityServer: "Servers",
        entityContactGroup: "Contact groups",
        tableUser: "User",
        tableAction: "Action",
        tableEntity: "Entity",
        tableDate: "Date",
        tableDetails: "Details",
        tableChanges: "Changes",
        dateFrom: "From date",
        dateTo: "To date",
        presetsTitle: "Quick range",
        presetToday: "Today",
        presetYesterday: "Yesterday",
        presetWeek: "Week",
        presetMonth: "Month",
        presetClear: "Reset",
        unknownUser: "Unknown user",
        unknownEntity: "Unknown entity",
        noDetails: "No additional details",
    },
    ru: {
        title: "История изменений",
        subTitle: "действия, выполненные в рабочем пространстве",
        homeURL: "/dashboard",
        searchPlaceholder: "Поиск по пользователю или действию...",
        filterLabel: "Сущность",
        allEntities: "Все сущности",
        entityWorkspace: "Рабочие области",
        entityDomain: "Домены",
        entityCertificate: "Сертификаты",
        entityWebsite: "Веб-сайты",
        entityServer: "Серверы",
        entityContactGroup: "Группы контактов",
        tableUser: "Пользователь",
        tableAction: "Действие",
        tableEntity: "Сущность",
        tableDate: "Дата",
        tableDetails: "Подробности",
        tableChanges: "Изменения",
        dateFrom: "С даты",
        dateTo: "По дату",
        presetsTitle: "Быстрый выбор",
        presetToday: "Сегодня",
        presetYesterday: "Вчера",
        presetWeek: "Неделя",
        presetMonth: "Месяц",
        presetClear: "Сбросить",
        unknownUser: "Неизвестный пользователь",
        unknownEntity: "Неизвестная сущность",
        noDetails: "Дополнительных сведений нет",
    },
    ua: {
        title: "Історія змін",
        subTitle: "дії, виконані у робочому просторі",
        homeURL: "/dashboard",
        searchPlaceholder: "Пошук за користувачем або дією...",
        filterLabel: "Сутність",
        allEntities: "Усі сутності",
        entityWorkspace: "Робочі простори",
        entityDomain: "Домени",
        entityCertificate: "Сертифікати",
        entityWebsite: "Вебсайти",
        entityServer: "Сервери",
        entityContactGroup: "Групи контактів",
        tableUser: "Користувач",
        tableAction: "Дія",
        tableEntity: "Сутність",
        tableDate: "Дата",
        tableDetails: "Деталі",
        tableChanges: "Зміни",
        dateFrom: "З дати",
        dateTo: "До дати",
        presetsTitle: "Швидкий вибір",
        presetToday: "Сьогодні",
        presetYesterday: "Вчора",
        presetWeek: "Тиждень",
        presetMonth: "Місяць",
        presetClear: "Скинути",
        unknownUser: "Невідомий користувач",
        unknownEntity: "Невідома сутність",
        noDetails: "Додаткових відомостей немає",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const entityFilterOptions = [
    { value: "all", labelKey: "allEntities" },
    { value: "workspace", labelKey: "entityWorkspace" },
    { value: "domain", labelKey: "entityDomain" },
    { value: "certificate", labelKey: "entityCertificate" },
    { value: "web_site", labelKey: "entityWebsite" },
    { value: "server", labelKey: "entityServer" },
    { value: "contact_group", labelKey: "entityContactGroup" },
];

const ChangeHistory = ({ lang: langProp }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "history";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});
    const [entityFilter, setEntityFilter] = useState("all");
    const [dateRange, setDateRange] = useState({ from: "", to: "", preset: "" });

    const entitySelectOptions = useMemo(
        () =>
            entityFilterOptions.map((option) => ({
                value: option.value,
                label: option.label || t[option.labelKey] || option.value,
            })),
        [t]
    );

    const listArgs = useMemo(() => {
        const args = {};
        if (entityFilter && entityFilter !== "all") {
            args.entity_type = entityFilter;
        }
        if (dateRange.from) {
            args.created_from = dateRange.from;
        }
        if (dateRange.to) {
            args.created_to = dateRange.to;
        }
        return args;
    }, [entityFilter, dateRange]);

    const entityLabelMap = useMemo(() => {
        const map = {};
        entitySelectOptions.forEach(({ value, label }) => {
            map[value] = label;
        });
        return map;
    }, [entitySelectOptions]);

    const formatLocalDate = (date) => {
        if (!(date instanceof Date)) return "";
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, "0");
        const day = `${date.getDate()}`.padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const presetButtons = useMemo(() => ([
        { key: "today", label: t.presetToday },
        { key: "yesterday", label: t.presetYesterday },
        { key: "week", label: t.presetWeek },
        { key: "month", label: t.presetMonth },
    ]), [t]);

    const clearPreset = useCallback(() => {
        setDateRange({ from: "", to: "", preset: "" });
    }, []);

    const applyPreset = useCallback((presetKey) => {
        if (presetKey === "clear") {
            clearPreset();
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let fromDate = "";
        let toDate = formatLocalDate(today);

        if (presetKey === "today") {
            fromDate = toDate;
        } else if (presetKey === "yesterday") {
            const prev = new Date(today);
            prev.setDate(prev.getDate() - 1);
            const formatted = formatLocalDate(prev);
            fromDate = formatted;
            toDate = formatted;
        } else if (presetKey === "week") {
            const prev = new Date(today);
            prev.setDate(prev.getDate() - 6);
            fromDate = formatLocalDate(prev);
        } else if (presetKey === "month") {
            const prev = new Date(today);
            prev.setDate(prev.getDate() - 29);
            fromDate = formatLocalDate(prev);
        } else {
            fromDate = "";
            toDate = "";
        }

        setDateRange({ from: fromDate, to: toDate, preset: presetKey });
    }, [clearPreset]);

    const handleDateChange = (field, value) => {
        setDateRange((prev) => ({ ...prev, [field]: value, preset: "custom" }));
    };

    const actionLabels = useMemo(() => ({
        en: {
            "created domain": "Created domain",
            "updated domain": "Updated domain",
            "deleted domain": "Deleted domain",
            "created certificate": "Created certificate",
            "updated certificate": "Updated certificate",
            "deleted certificate": "Deleted certificate",
            "created website": "Created website",
            "updated website": "Updated website",
            "deleted website": "Deleted website",
            "created server": "Created server",
            "updated server": "Updated server",
            "deleted server": "Deleted server",
            "created contact group": "Created contact group",
            "updated contact group": "Updated contact group",
            "deleted contact group": "Deleted contact group",
        },
        ru: {
            "created domain": "Создан домен",
            "updated domain": "Обновлён домен",
            "deleted domain": "Удалён домен",
            "created certificate": "Создан сертификат",
            "updated certificate": "Обновлён сертификат",
            "deleted certificate": "Удалён сертификат",
            "created website": "Создан сайт",
            "updated website": "Обновлён сайт",
            "deleted website": "Удалён сайт",
            "created server": "Создан сервер",
            "updated server": "Обновлён сервер",
            "deleted server": "Удалён сервер",
            "created contact group": "Создана группа контактов",
            "updated contact group": "Обновлена группа контактов",
            "deleted contact group": "Удалена группа контактов",
        },
        ua: {
            "created domain": "Створено домен",
            "updated domain": "Оновлено домен",
            "deleted domain": "Видалено домен",
            "created certificate": "Створено сертифікат",
            "updated certificate": "Оновлено сертифікат",
            "deleted certificate": "Видалено сертифікат",
            "created website": "Створено сайт",
            "updated website": "Оновлено сайт",
            "deleted website": "Видалено сайт",
            "created server": "Створено сервер",
            "updated server": "Оновлено сервер",
            "deleted server": "Видалено сервер",
            "created contact group": "Створено групу контактів",
            "updated contact group": "Оновлено групу контактів",
            "deleted contact group": "Видалено групу контактів",
        },
    }), []);

    const resolveActionLabel = useCallback((action = "") => {
        if (!action) return "";
        const normalized = action.toLowerCase().trim();
        const dictionary = actionLabels[lang] || actionLabels.en || {};
        return dictionary[normalized] || dictionary[action] || action;
    }, [actionLabels, lang]);

    const formatEntityLabel = (value) => {
        if (!value) return t.unknownEntity;
        const normalized = String(value).toLowerCase();
        if (entityLabelMap[normalized]) {
            return entityLabelMap[normalized];
        }
        return normalized
            .split(/[\s_]+/)
            .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1))
            .join(" ");
    };

    const getSummaryText = useCallback((entry = {}) => {
        const summary = entry?.changes?.summary;
        if (summary) {
            return summary[lang] || summary.en || entry.details || t.noDetails;
        }
        return entry.details || t.noDetails;
    }, [lang, t]);

    const formatFieldChange = useCallback((item = {}) => {
        return (
            item.i18n?.[lang] ||
            item.i18n?.en ||
            `${(item.label?.[lang] || item.label?.en || item.key)}: ${item.from} → ${item.to}`
        );
    }, [lang]);

    const columns = useMemo(
        () => [
            {
                label: t.tableDate,
                key: "created_at",
                format: (data) => (
                    <span className="fw-semibold text-nowrap">{data.created_at || "—"}</span>
                ),
            },
            {
                label: t.tableUser,
                key: "user_name",
                format: (data) => (
                    <div>
                        <div className="fw-semibold">{data.user_name || t.unknownUser}</div>
                        {data.user_email && <div className="text-muted small">{data.user_email}</div>}
                    </div>
                ),
            },
            {
                label: t.tableEntity,
                key: "entity_type",
                format: (data) => (
                    <div>
                        <span className="badge bg-light text-dark text-uppercase">
                            {formatEntityLabel(data.entity_type_key || data.entity_type)}
                        </span>
                    </div>
                ),
            },
            {
                label: t.tableAction,
                key: "action",
                format: (data) => (
                    <span className="text-capitalize">{resolveActionLabel(data.action) || "—"}</span>
                ),
            },
            {
                label: t.tableDetails,
                key: "details",
                format: (data) => (
                    <div>
                        <div className="fw-semibold">{data.entity_label || t.unknownEntity}</div>
                        <div className="text-muted small">{getSummaryText(data)}</div>
                        {Array.isArray(data?.changes?.fields) && data.changes.fields.length > 0 && (
                            <div className="mt-2">
                                <div className="text-muted small fw-semibold">{t.tableChanges}:</div>
                                <ul className="mb-0 ps-3 text-muted small">
                                    {data.changes.fields.map((item, index) => (
                                        <li key={`${item.key}-${index}`}>{formatFieldChange(item)}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                ),
            },
        ],
        [t, entityLabelMap, getSummaryText, formatFieldChange, resolveActionLabel]
    );

    const searchFields = useMemo(
        () => ({
            simple: [
                {
                    type: "tab",
                    name: "",
                    image: "",
                    fields: [
                        [
                            {
                                type: "custom",
                                fn: () => (
                                    <div className="mb-2">
                                        <label className="form-label fw-semibold">{t.filterLabel}</label>
                                        <select
                                            className="form-select"
                                            value={entityFilter}
                                            onChange={(event) => setEntityFilter(event.target.value)}
                                        >
                                            {entitySelectOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ),
                            },
                        ],
                        [
                            {
                                type: "custom",
                                fn: () => (
                                    <div className="mb-2">
                                        <div className="row row-cols-1 row-cols-md-3 g-2 g-md-3 align-items-end">
                                            <div className="col">
                                                <label className="form-label fw-semibold w-100">{t.dateFrom}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={dateRange.from}
                                                    onChange={(event) => handleDateChange("from", event.target.value)}
                                                />
                                            </div>
                                            <div className="col">
                                                <label className="form-label fw-semibold w-100">{t.dateTo}</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={dateRange.to}
                                                    onChange={(event) => handleDateChange("to", event.target.value)}
                                                />
                                            </div>
                                            <div className="col">
                                                <label className="form-label fw-semibold w-100">{t.presetsTitle}</label>
                                                <div className="d-flex flex-wrap gap-2">
                                                    {presetButtons.map((btn) => (
                                                        <button
                                                            key={btn.key}
                                                            type="button"
                                                            className={`btn btn-outline-secondary btn-sm ${
                                                                dateRange.preset === btn.key ? "active" : ""
                                                            }`}
                                                            onClick={() => applyPreset(btn.key)}
                                                        >
                                                            {btn.label}
                                                        </button>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={() => applyPreset("clear")}
                                                    >
                                                        {t.presetClear}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ),
                            },
                        ],
                        [
                            {
                                label: t.searchPlaceholder,
                                key: "text",
                                beforeField: (
                                    <i
                                        style={{ top: "14px" }}
                                        className="ti ti-search position-absolute start-0 translate-middle-y fs-6 text-dark ms-3 relative"
                                    ></i>
                                ),
                                classField: "product-search ps-5",
                                styleField: { marginTop: "-20px" },
                            },
                        ],
                    ],
                },
            ],
        }),
        [t, entityFilter, entitySelectOptions, dateRange, presetButtons, applyPreset, handleDateChange]
    );

    return (
        <BasicCRUD
            title={t.title}
            subTitle={t.subTitle}
            homeURL={t.homeURL}
            object={object}
            columns={columns}
            fields={[]}
            actions={[]}
            fieldsData={fieldsData}
            setFieldsData={setFieldsData}
            response={response}
            setResponse={setResponse}
            ownHandler={true}
            args={listArgs}
            filters={listArgs}
            hideActions={true}
            hideGroupActions={true}
            searchFields={searchFields}
            viewModes={["table", "blocks"]}
            defaultViewMode="table"
            renderItem={(item) => (
                <div className="card h-100 shadow-sm border">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="badge bg-light text-dark text-uppercase">
                                {formatEntityLabel(item.entity_type_key || item.entity_type)}
                            </span>
                            <span className="text-muted small">{item.created_at || "—"}</span>
                        </div>
                        <h6 className="fw-semibold mb-1">{resolveActionLabel(item.action)}</h6>
                        <p className="text-muted mb-2">
                            {item.user_name || t.unknownUser}
                            {item.user_email && <><br /><span className="small">{item.user_email}</span></>}
                        </p>
                        <div className="fw-semibold mb-1">{item.entity_label || t.unknownEntity}</div>
                        <div className="text-muted small">{getSummaryText(item)}</div>
                        {Array.isArray(item?.changes?.fields) && item.changes.fields.length > 0 && (
                            <ul className="mt-2 mb-0 ps-3 text-muted small">
                                {item.changes.fields.map((change, idx) => (
                                    <li key={`${change.key}-${idx}`}>{formatFieldChange(change)}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}
        />
    );
};

export default ChangeHistory;
